import React, { useState, useEffect } from 'react';
import { API_URL, getImageUrl } from '../../utils/api';
import LearningStatusBadge from '../ui/LearningStatusBadge';
import { LoadingSpinner } from '../ui/Icons';
import Confetti from 'react-confetti';
import ImageCarousel from '../common/ImageCarousel';
import { GraduationCap, Timer, Zap } from 'lucide-react';

import QuizLayout from './shared/QuizLayout';
import QuizHeader from './shared/QuizHeader';

const BlitzQuiz = ({ deck, onFinish, onReview, trackInteraction, userPlantData, onAbort, plants, theme }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [options, setOptions] = useState([]);
    const [timeLeft, setTimeLeft] = useState(20);
    const [isActive, setIsActive] = useState(true);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [results, setResults] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);

    const currentCard = deck[currentIndex];
    const userCardData = userPlantData?.[currentCard?.id] || {};
    const currentStatus = userCardData.learning_status || 'NOT_LEARNED';

    useEffect(() => {
        if (!currentCard || !plants) return;
        const distractors = plants.filter(p => p.category === currentCard.category && p.id !== currentCard.id)
            .sort(() => 0.5 - Math.random()).slice(0, 3);
        setOptions([currentCard, ...distractors].sort(() => 0.5 - Math.random()));

        setTimeLeft(20);
        setIsActive(true);
        setSelectedAnswer(null);
        setIsAnswered(false);
    }, [currentIndex, currentCard, plants]);

    useEffect(() => {
        let interval = null;
        if (isActive && timeLeft > 0 && !isAnswered) {
            interval = setInterval(() => setTimeLeft(t => t - 1), 1000);
        } else if (timeLeft === 0 && !isAnswered) {
            handleAnswer(null);
        }
        return () => clearInterval(interval);
    }, [isActive, timeLeft, isAnswered]);

    const handleAnswer = (option) => {
        if (isAnswered) return;
        setIsAnswered(true);
        setSelectedAnswer(option);

        const correct = option && option.id === currentCard.id;
        const answerTime = 20 - timeLeft;
        const result = { plant: currentCard, correct, userAnswer: option, time: answerTime };
        const updatedResults = [...results, result];
        setResults(updatedResults);

        if (correct) {
            setScore(p => p + 1);
            setStreak(p => { const s = p + 1; if (s > bestStreak) setBestStreak(s); return s; });
        } else {
            setStreak(0);
        }

        if (onReview) onReview(currentCard, correct, null, answerTime);

        if (trackInteraction) {
            trackInteraction(correct ? 'FLASHCARD_CORRECT' : 'FLASHCARD_INCORRECT', {
                plant_id: currentCard.id,
                time_taken: answerTime,
                mode: 'blitz'
            });
        }
        setTimeout(() => {
            if (currentIndex < deck.length - 1) setCurrentIndex(p => p + 1);
            else onFinish(updatedResults);
        }, correct ? 1000 : 2000);
    };

    if (!currentCard) return <LoadingSpinner />;

    const timePercentage = (timeLeft / 20) * 100;

    return (
        <QuizLayout onClose={onAbort} theme={theme} className="flex flex-col overflow-hidden">
            {isAnswered && selectedAnswer?.id === currentCard.id && <Confetti numberOfPieces={100} recycle={false} gravity={0.3} style={{ zIndex: 100 }} />}

            {/* Premium Header */}
            <QuizHeader
                current={currentIndex + 1}
                total={deck.length}
                onClose={onAbort}
                status={currentStatus}
                theme={theme}
            />

            {/* Timer Progress Bar (Elite Style) */}
            <div className="absolute top-0 left-0 w-full h-1.5 z-[70] overflow-hidden">
                <div 
                    className={`h-full transition-all duration-1000 ease-linear ${timeLeft < 5 ? 'bg-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.8)]' : 'bg-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.4)]'}`}
                    style={{ width: `${timePercentage}%` }}
                />
            </div>

            {/* Workspace */}
            <div className="flex-1 flex flex-col min-h-0 space-y-3 px-4 md:px-12 pb-4 md:pb-8">
                {/* Image Container with Elegant Shadow */}
                <div className="flex-1 min-h-0 relative w-full aspect-[21/9] md:aspect-[21/9] bg-slate-900 rounded-[2rem] overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] border border-white/10 group">
                    <ImageCarousel
                        images={currentCard.images}
                        alt={currentCard.german_name}
                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-90"
                        showThumbnails={false}
                    />

                    {/* Stats Overlays */}
                    <div className="absolute top-3 inset-x-3 flex justify-between z-40">
                         <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/90 backdrop-blur-xl border border-amber-400/50 rounded-xl shadow-lg text-white">
                            <Zap className={`w-4 h-4 ${streak > 2 ? 'fill-white animate-pulse' : 'fill-white/20'}`} />
                            <div className="flex flex-col">
                                <span className="text-[8px] font-black uppercase tracking-[0.2em] opacity-80 leading-none">Streak</span>
                                <span className="text-xs font-black tracking-tighter tabular-nums leading-none">{streak}</span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 px-4 py-2 bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl">
                             <Timer className={`w-4 h-4 ${timeLeft < 5 ? 'text-rose-500 animate-pulse' : 'text-white'}`} />
                             <span className={`text-md font-black tracking-tighter tabular-nums ${timeLeft < 5 ? 'text-rose-500' : 'text-white'}`}>{timeLeft}s</span>
                        </div>
                    </div>

                    {/* Question Overlay */}
                    <div className="absolute bottom-3 inset-x-3 p-3 md:p-6 bg-white/5 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] shadow-2xl text-center z-30">
                        <span className="text-[8px] uppercase tracking-[0.4em] font-black text-amber-400 mb-1 block drop-shadow-md">Elite-Blitz</span>
                        <h3 className="text-xl md:text-3xl font-black text-white leading-tight tracking-tight italic">
                            Wer bin ich?
                        </h3>
                    </div>
                </div>

                {/* Options Area */}
                <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    {options.map((opt, idx) => {
                        const isDark = theme === 'dark';
                        let optionClass = isDark
                            ? "bg-white/5 backdrop-blur-xl border border-white/10 hover:border-amber-400/50 hover:bg-white/15 text-white shadow-xl"
                            : "bg-white/60 backdrop-blur-xl border border-slate-200 hover:border-amber-400/50 hover:bg-white text-slate-900 shadow-md";

                        if (isAnswered) {
                            if (opt.id === currentCard.id) {
                                optionClass = "bg-plantlix-500 border-plantlix-400 text-white shadow-[0_0_30px_rgba(39,145,106,0.4)] ring-4 ring-plantlix-500/20 z-10 scale-[1.02]";
                            } else if (selectedAnswer && opt.id === selectedAnswer.id) {
                                optionClass = "bg-rose-500 border-red-400 text-white shadow-[0_0_30px_rgba(239,68,68,0.4)] opacity-95 transition-none";
                            } else {
                                optionClass = isDark
                                    ? "bg-slate-900/40 border-transparent text-white/20 opacity-40 scale-[0.98]"
                                    : "bg-slate-100/40 border-transparent text-slate-300 opacity-40 scale-[0.98]";
                            }
                        }

                        return (
                            <button
                                key={idx}
                                onClick={() => handleAnswer(opt)}
                                disabled={isAnswered}
                                className={`
                                    relative px-5 py-3 md:py-5 rounded-2xl md:rounded-[2rem] text-left transition-all duration-300 active:scale-95 flex items-center justify-between group overflow-hidden
                                    ${optionClass}
                                `}
                            >
                                <div className="flex flex-col min-w-0 pr-4">
                                    <span className="font-black text-lg md:text-2xl leading-tight truncate italic tracking-tight">
                                        {opt.botanical_name}
                                    </span>
                                    <span className="text-[9px] font-black uppercase opacity-40 tracking-[0.2em] truncate mt-0.5">
                                        {opt.german_name}
                                    </span>
                                </div>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border border-white/10 bg-white/5 transition-all
                                    ${isAnswered && opt.id === currentCard.id ? 'bg-white text-plantlix-600 border-transparent shadow-lg' : ''}
                                    ${isAnswered && selectedAnswer && opt.id === selectedAnswer.id && opt.id !== currentCard.id ? 'bg-white text-rose-500 border-transparent' : ''}
                                `}>
                                    {isAnswered ? (
                                        opt.id === currentCard.id ? <span className="text-lg font-black">✓</span> :
                                        (selectedAnswer?.id === opt.id ? <span className="text-lg font-black">✗</span> : null)
                                    ) : (
                                        <div className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-amber-400 group-hover:scale-150 transition-all" />
                                    )}
                                </div>
                                <div className={`absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${isAnswered ? 'hidden' : ''}`} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </QuizLayout>
    );
};

export default BlitzQuiz;
